import { useEffect, useId, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faFileText, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { selectChatDocuments, selectUploadingDocumentIDs } from "app/modules/aiAssistant/AIAssistantSlice";
import { deleteChatDocument } from "app/modules/aiAssistant/AIAssistantThunks";
import useDismiss from "app/modules/aiAssistant/page/hooks/useDismiss";
import "app/modules/aiAssistant/styles/shared.scss";
import "./ChatDocumentsMenu.scss";

// Every document in the open chat, whichever message it was sent with
const ChatDocumentsMenu = () => {
  const thunkDispatch = useThunkDispatch();
  const chatDocuments = useSelector(selectChatDocuments);
  const uploadingDocumentIDs = useSelector(selectUploadingDocumentIDs);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuID = useId();

  const documents = chatDocuments.filter((d) => !uploadingDocumentIDs.includes(d.id));

  useDismiss([containerRef], isOpen, (returnFocus) => {
    setIsOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  });

  // Removing the last document leaves nothing to show
  useEffect(() => {
    if (documents.length === 0) setIsOpen(false);
  }, [documents.length]);

  if (documents.length === 0) return null;

  return (
    <div ref={containerRef} className="assistimo-documents-menu">
      <button
        ref={buttonRef}
        data-test-id="assistimo-page__documents-menu-button"
        type="button"
        className="assistimo-documents-menu__button"
        aria-expanded={isOpen}
        aria-controls={menuID}
        onClick={() => setIsOpen((open) => !open)}
      >
        <FontAwesomeIcon icon={faFileText} />
        Documents ({documents.length})
        <FontAwesomeIcon icon={faChevronDown} className="assistimo-documents-menu__chevron" />
      </button>
      {isOpen && (
        <ul id={menuID} className="assistimo-documents-menu__list">
          {documents.map((document) => (
            <li key={document.id} className="assistimo-documents-menu__item">
              <FontAwesomeIcon icon={faFileText} className="assistimo-documents-menu__icon" />
              <span className="assistimo-documents-menu__name" title={document.name}>
                {document.name}
              </span>
              <button
                data-test-id={`assistimo-page__remove-chat-document-button-${document.id}`}
                type="button"
                className="assistimo-icon-button assistimo-icon-button--small"
                aria-label={`Remove ${document.name}`}
                onClick={() => thunkDispatch(deleteChatDocument(document.id))}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ChatDocumentsMenu;
